/**
 * ringBuffer — deterministic CPU-side spawn-slot allocation for the GPU pool.
 *
 * v1 trade-off (documented): the pool does NOT use a GPU free-list or atomics.
 * Instead the CPU advances a monotonic spawn cursor and hands the spawn kernel
 * a contiguous `[start, count)` slot range each emit batch, wrapping around the
 * fixed power-of-two capacity. New particles therefore overwrite the OLDEST
 * slots once the buffer fills.
 *
 * Consequences:
 *   • Allocation is O(1) and fully deterministic given the emit schedule.
 *   • If emission rate × lifetime exceeds capacity, the newest particles
 *     stomp still-alive old ones early (visible as a hard cap on density).
 *     Size the pool ≥ rate × maxLifetime to avoid this.
 *   • A batch that would cross the capacity boundary is SPLIT into two
 *     contiguous ranges so the spawn kernel never needs modulo-indexed writes.
 *
 * A genuine GPU free-list (atomic alloc + dead-slot recycling) is a v2 upgrade;
 * the kernel-facing contract (a list of contiguous ranges) stays the same.
 */

/** A contiguous half-open slot range `[start, start + count)`. */
export interface SpawnRange {
  start: number;
  count: number;
}

export class SpawnRingBuffer {
  /** Power-of-two capacity. */
  readonly capacity: number;
  private _cursor = 0;
  /** Monotonic count of slots ever allocated (for stats / estimates). */
  private _totalAllocated = 0;

  constructor(capacity: number) {
    if (capacity <= 0 || (capacity & (capacity - 1)) !== 0) {
      throw new Error(`SpawnRingBuffer: capacity must be a positive power of two, got ${capacity}`);
    }
    this.capacity = capacity;
  }

  /** Current write cursor (next slot that will be allocated). */
  get cursor(): number { return this._cursor; }

  /** Total slots ever allocated across the buffer's lifetime. */
  get totalAllocated(): number { return this._totalAllocated; }

  /** Reset the cursor (used by `stop()`). */
  reset(): void {
    this._cursor = 0;
    this._totalAllocated = 0;
  }

  /**
   * Allocate `n` slots, returning 1 or 2 contiguous ranges (2 only when the
   * allocation wraps past the capacity boundary). Advances the cursor.
   *
   * `n` is clamped to `capacity` — asking for more than the whole buffer at
   * once is meaningless (the surplus would immediately overwrite itself).
   */
  allocate(n: number): SpawnRange[] {
    const count = Math.min(Math.max(0, Math.floor(n)), this.capacity);
    if (count === 0) return [];

    const start = this._cursor;
    const end = start + count;
    this._totalAllocated += count;

    if (end <= this.capacity) {
      // No wrap — single contiguous range.
      this._cursor = end === this.capacity ? 0 : end;
      return [{ start, count }];
    }

    // Wraps the boundary — split into head [start, capacity) + tail [0, rem).
    const head = this.capacity - start;
    const tail = count - head;
    this._cursor = tail; // tail < capacity (count ≤ capacity), so no double-wrap
    return [
      { start, count: head },
      { start: 0, count: tail },
    ];
  }
}

/**
 * Round a requested capacity up to the next power of two, clamped to
 * `[min, max]`. The GPU pool requires pow2 so the ring-buffer math and any
 * future bitmask indexing stay branch-free.
 */
export function nextPow2(value: number, min = 256, max = 1 << 22): number {
  const clampedLo = Math.max(min, Math.floor(value));
  let p = min;
  while (p < clampedLo && p < max) p <<= 1;
  return Math.min(p, max);
}
